import { useState, useRef, useCallback, useEffect } from 'react';
import { Question } from '@/types/theme';
import { directAIClient, AIValidationResult } from '@/integrations/ai/direct-client';
import { logValidationStarted, logValidationCompleted, logValidationError, logValidationSkipped } from '@/utils/validationLogger';

/**
 * Hook for validating user responses using AI
 * Follows IntentDrive.ai established hook patterns
 * Now uses direct API calls instead of Edge Functions
 */
export function useAIValidation() {
  const [isValidating, setIsValidating] = useState(false);
  const validationAttemptsRef = useRef<Record<string, number>>({});

  /**
   * Validate a user response against a question using AI
   * @param question - The question being answered
   * @param response - The user's response to validate
   * @param options - Validation configuration options
   * @returns Promise<AIValidationResult> - Validation result with confidence score
   */
  const validateResponse = useCallback(async (
    question: Question,
    response: string,
    options: {
      chatbotId?: string;
      sessionId?: string;
      strictMode?: boolean;
      maxAttempts?: number;
    } = {}
  ): Promise<AIValidationResult> => {
    const { chatbotId, sessionId, strictMode = false, maxAttempts = 3 } = options;

    // Track validation attempts for this question
    const questionKey = `${chatbotId || 'default'}-${question.id}`;
    const currentAttempt = (validationAttemptsRef.current[questionKey] || 0) + 1;
    validationAttemptsRef.current[questionKey] = currentAttempt;

    setIsValidating(true);

    // Skip validation for certain question types
    if (['select', 'button', 'no_response'].includes(question.type)) {
      logValidationSkipped(question, response, `Question type ${question.type} doesn't require validation`, chatbotId, sessionId);
      return {
        isValid: true,
        confidenceScore: 1.0,
        suggestedFollowUp: "Thank you for your response."
      };
    }

    // Skip validation if validationType is 'none'
    if (question.validationType === 'none') {
      logValidationSkipped(question, response, "Validation is disabled for this question", chatbotId, sessionId);
      return {
        isValid: true,
        confidenceScore: 1.0,
        suggestedFollowUp: "Thank you for your response."
      };
    }

    try {
      // Log validation start
      logValidationStarted(question, response, currentAttempt, maxAttempts, chatbotId, sessionId);

      // Use direct AI client for validation
      const validationResult = await directAIClient.validateResponse({
        question: question.text,
        questionType: question.type,
        response,
        chatbotId,
        validationSettings: {
          strictValidation: strictMode || question.validation_rules?.strictValidation || false,
          confidenceThreshold: question.validation_rules?.confidenceThreshold || 0.7,
          allowPartialMatches: question.validation_rules?.allowPartialMatches || false
        }
      });

      // Log validation completion
      logValidationCompleted(question, response, validationResult, currentAttempt, maxAttempts, chatbotId, sessionId);

      return validationResult;

    } catch (error) {
      console.error('❌ AI validation error:', error);
      logValidationError(question, response, error, currentAttempt, maxAttempts, chatbotId, sessionId);

      // Return permissive validation on error
      const fallbackResult: AIValidationResult = {
        isValid: true,
        confidenceScore: 0.5,
        suggestedFollowUp: "Thank you for your response. Let's continue."
      };

      return fallbackResult;

    } finally {
      setIsValidating(false);
    }
  }, []);

  /**
   * Reset validation attempts for a specific question
   * @param questionId - The question ID to reset
   * @param chatbotId - Optional chatbot ID for scoping
   */
  const resetValidationAttempts = useCallback((questionId: string, chatbotId?: string) => {
    const questionKey = `${chatbotId || 'default'}-${questionId}`;
    delete validationAttemptsRef.current[questionKey];
  }, []);

  /**
   * Get the number of validation attempts for a question
   * @param questionId - The question ID
   * @param chatbotId - Optional chatbot ID for scoping
   * @returns Number of attempts made
   */
  const getValidationAttempts = useCallback((questionId: string, chatbotId?: string): number => {
    const questionKey = `${chatbotId || 'default'}-${questionId}`;
    return validationAttemptsRef.current[questionKey] || 0;
  }, []);

  useEffect(() => {
    return () => {
      // Clean up validation attempts on unmount
      validationAttemptsRef.current = {};
    };
  }, []);

  return {
    validateResponse,
    isValidating,
    resetValidationAttempts,
    getValidationAttempts
  };
}
